{
    "defaults": {},
    "completion": {
        "title": "You're all set",
        "body": "That's the essentials. You can replay this tour anytime from the Help menu in the top navigation bar."
    },
    "moduleOrder": [
        "welcome",
        "landing-page",
        "device-management",
        "faults",
        "rfid",
        "rfid-create-edit",
        "dashboard",
        "reports",
        "schedule-reports"
    ],
    "modules": {
        "welcome": {
            "label": "Welcome",
            "enabled": true,
            "steps": [
                {
                    "kind": "welcome",
                    "title": "Welcome to Illuminate",
                    "body": {
                        "customer": "Illuminate keeps your EV chargers running and your drivers moving through real-time visibility and rapid issue resolution. This quick tour shows you where to find the essentials.",
                        "default": "Illuminate helps your team keep EV chargers running and drivers moving through real-time visibility and rapid issue resolution. This quick tour shows you where to find the essentials."
                    }
                }
            ]
        },
        "landing-page": {
            "label": "Home page",
            "enabled": true,
            "estimatedMinutes": 1,
            "steps": [
                {
                    "kind": "navigation",
                    "title": "Home",
                    "body": "Go to the Home page to begin.",
                    "interaction": "Click Home to continue.",
                    "selector": "[data-tour-step=\"landing-page-home\"]",
                    "interactive": true,
                    "advanceOnNavigation": true,
                    "advanceAtRoute": "/"
                },
                {
                    "title": "Navigation",
                    "body": "Use the navigation bar to move between your network, sites, devices and more. You can return to Home at any time.",
                    "selector": "[data-tour-step=\"landing-page-navigation\"]",
                    "noBack": true
                },
                {
                    "title": "Filter your network",
                    "body": {
                        "partner": "Use the organisation filter to focus on a specific part of your network and explore its performance and trends.",
                        "default": "Use the organisation or service model filter to focus on a specific part of Illuminate's network and explore its performance and trends."
                    },
                    "interaction": "Try selecting an organisation to see the details on the page change.",
                    "requiredConditions": ["multi_org"],
                    "interactive": true,
                    "selector": "[data-tour-step=\"landing-page-filter\"]",
                    "highlightedSelectors": [
                        "[data-tour-step=\"landing-page-filter\"]",
                        "[data-radix-popper-content-wrapper]"
                    ],
                    "position": "left"
                },
                {
                    "title": "Network status",
                    "body": "View the real-time status of your devices and connectors so you can quickly identify anything that needs attention.",
                    "selector": "[data-tour-step=\"landing-page-status\"]"
                },
                {
                    "title": "Network trends",
                    "body": "Monitor network performance over time and identify trends that may require attention.",
                    "interaction": "Select a card to see detailed uptime, energy usage, and session activity.",
                    "selector": "[data-tour-step=\"landing-page-summary\"]",
                    "interactive": true,
                    "actionKey": "flash-on",
                    "actionAfterKey": "flash-off",
                    "flashKey": "summary-card",
                    "requiredConditions": ["single_org", "multi_org"],
                    "position": "center"
                }
            ]
        },
        "faults": {
            "label": "Faults",
            "enabled": false,
            "requiredPermissions": ["ticketing_read"],
            "steps": []
        },
        "device-management": {
            "label": "Device management",
            "enabled": false,
            "requiredPermissions": ["device_location_read"],
            "steps": []
        },
        "rfid": {
            "label": "RFID management",
            "enabled": true,
            "estimatedMinutes": 1,
            "requiredPermissions": ["rfid_tags_read"],
            "steps": [
                {
                    "kind": "navigation",
                    "title": "Open Settings",
                    "body": "Access Settings to manage RFID tags and other network configuration options.",
                    "interaction": "Click the settings icon to continue.",
                    "selector": "[data-tour-step=\"settings-navigation\"]",
                    "interactive": true,
                    "advanceOnNavigation": true,
                    "advanceAtRoute": "/settings"
                },
                {
                    "kind": "navigation",
                    "title": "RFID management",
                    "body": {
                        "customer": "Manage RFID tags to control who can charge on your network.",
                        "default": "Manage RFID tags to control who can charge on the network."
                    },
                    "interaction": "Click the RFID tags tab to continue.",
                    "selector": "[data-tour-step=\"rfid-management-tab\"]",
                    "interactive": true,
                    "advanceOnNavigation": true,
                    "advanceAtRoute": "/settings/rfid"
                },
                {
                    "title": "RFID tags",
                    "body": "View all RFID tags in your network and monitor their current status.",
                    "selector": "[data-tour-step=\"rfid-management-list\"]",
                    "position": "center",
                    "overflowPosition": "top-center",
                    "noBack": true
                }
            ]
        },
        "rfid-create-edit": {
            "label": "Create and edit RFIDs",
            "enabled": true,
            "estimatedMinutes": 1,
            "dependsOn": "rfid",
            "requiredPermissions": ["rfid_tags_manage"],
            "steps": [
                {
                    "title": "Add a new RFID tag",
                    "body": "Create a new RFID tag and assign it to a driver. Tags can be enabled or disabled at any time.",
                    "interaction": "Click the Create RFID button to open the form.",
                    "selector": "[data-tour-step=\"rfid-create-tag\"]",
                    "interactive": true,
                    "advanceOnClick": true
                },
                {
                    "title": "Tag ID",
                    "body": "Enter the RFID tag serial number. This is typically printed on the tag or captured when the tag is scanned. Each serial number is unique and cannot be changed once created.",
                    "selector": "[data-tour-step=\"rfid-create-tag-id\"]",
                    "interactive": true,
                    "position": "left"
                },
                {
                    "title": "Tag label",
                    "body": "Give the tag a recognisable label, such as a vehicle registration or driver's name, to make it easy to identify.",
                    "selector": "[data-tour-step=\"rfid-create-tag-label\"]",
                    "interactive": true,
                    "position": "left"
                },
                {
                    "title": "Configure the RFID tag",
                    "body": "Set the tag status and restrict access by assigning it to specific sites or users.",
                    "selector": "[data-tour-step=\"rfid-create-tag-form\"]",
                    "highlightedSelectors": [
                        "[data-tour-step=\"rfid-create-tag-form\"]",
                        "[data-radix-popper-content-wrapper]"
                    ],
                    "interactive": true,
                    "position": "left"
                },
                {
                    "title": "Next steps",
                    "body": "You can now create RFID tags to manage access across your EV charging network.",
                    "interaction": "Click cancel to continue.",
                    "selector": "[data-tour-step=\"rfid-create-tag-cancel\"]",
                    "advanceOnClick": true,
                    "interactive": true,
                    "position": "left"
                },
                {
                    "title": "Edit RFID tags",
                    "body": "Select an RFID tag from the list, then click Edit to update its details or status.",
                    "hint": {
                        "customer": "You can also select the edit option directly from the RFID tag list."
                    },
                    "selector": "[data-tour-step=\"rfid-edit-tag\"]",
                    "noBack": true
                }
            ]
        },
        "dashboard": {
            "label": "Dashboards",
            "enabled": false,
            "requiredPermissions": ["device_energy_usage_read"],
            "steps": []
        },
        "reports": {
            "label": "Reports",
            "enabled": true,
            "estimatedMinutes": 2,
            "requiredPermissions": ["charge_transactions_read"],
            "steps": [
                {
                    "kind": "navigation",
                    "title": "Open My Network",
                    "body": "Navigate to My Network to create, view and schedule reports.",
                    "interaction": "Click My network to continue",
                    "selector": "[data-tour-step=\"my-network-navigation\"]",
                    "interactive": true,
                    "advanceOnNavigation": true,
                    "advanceAtRoute": "/network"
                },
                {
                    "kind": "navigation",
                    "title": "Create a report",
                    "body": "Create a report to view or download data from your network.",
                    "interaction": "Click the create report tab to continue.",
                    "selector": "[data-tour-step=\"create-report-tab\"]",
                    "interactive": true,
                    "advanceOnNavigation": true,
                    "advanceAtRoute": "/network/reports/create",
                    "position": "right"
                },
                {
                    "kind": "standard",
                    "title": "Select a report type",
                    "body": "Start by selecting the type of report you want to generate. Each report provides different insights into your network activity and performance.",
                    "interaction": "Select Session Report and then continue.",
                    "selector": "[data-tour-step=\"create-report-type-select\"]",
                    "highlightedSelectors": [
                        "[data-tour-step=\"create-report-type-select\"]",
                        "[data-radix-popper-content-wrapper]"
                    ],
                    "interactive": true,
                    "position": "right",
                    "noBack": true
                },
                {
                    "kind": "standard",
                    "title": "Select a date range",
                    "body": "Choose the time period you want to include in your report. You can use a preset range or define a custom date range.",
                    "interaction": "Select the last 7 days preset to continue.",
                    "selector": "[data-tour-step=\"create-report-date-range-select\"]",
                    "highlightedSelectors": [
                        "[data-tour-step=\"create-report-date-range-select\"]",
                        "[data-radix-popper-content-wrapper]"
                    ],
                    "interactive": true,
                    "position": "right"
                },
                {
                    "kind": "standard",
                    "title": "Select devices",
                    "body": {
                        "customer": "Select your organisation to generate a report for your entire network, or choose a device group to focus on a subset of chargers.",
                        "default": "Select an organisation to generate a report for the entire network, or choose a device group to focus on a subset of chargers."
                    },
                    "interaction": {
                        "customer": "Select your organisation and then continue.",
                        "default": "Select an organisation and then continue."
                    },
                    "selector": "[data-tour-step=\"create-report-device-group\"]",
                    "highlightedSelectors": [
                        "[data-tour-step=\"create-report-device-group\"]",
                        "[data-radix-popper-content-wrapper]"
                    ],
                    "interactive": true,
                    "position": "right"
                },
                {
                    "kind": "standard",
                    "title": "Run the report",
                    "body": "View the report on screen or download the results as a CSV file.",
                    "interaction": "Click the View button to continue.",
                    "selector": "[data-tour-step=\"create-report-view-button\"]",
                    "interactive": true,
                    "position": "right",
                    "advanceOnClick": true
                },
                {
                    "kind": "standard",
                    "title": "Review results",
                    "body": "Review report data based on the report type, date range and devices selected. Download the results as a CSV file for further analysis.",
                    "interaction": "Select a summary tab to drill down into the data.",
                    "selector": "[data-tour-step=\"create-report-page\"]",
                    "highlightedSelectors": [
                        "[data-tour-step=\"create-report-page\"]",
                        "[data-radix-popper-content-wrapper]"
                    ],
                    "interactive": true,
                    "actionKey": "flash-on",
                    "actionAfterKey": "flash-off",
                    "flashKey": "report-slug",
                    "position": "right",
                    "overflowPosition": "top-center"
                }
            ]
        },
        "schedule-reports": {
            "label": "Schedule reports",
            "enabled": true,
            "estimatedMinutes": 1,
            "dependsOn": "reports",
            "requiredPermissions": ["webhook_create"],
            "steps": [
                {
                    "kind": "navigation",
                    "title": "Schedule a report",
                    "body": "Schedule reports to be delivered to your inbox automatically on a daily, weekly, or monthly basis. Open Scheduled Reports to create, manage, and view your scheduled reports.",
                    "interaction": "Click the scheduled reports tab to continue.",
                    "selector": "[data-tour-step=\"scheduled-reports-tab\"]",
                    "interactive": true,
                    "advanceOnNavigation": true,
                    "advanceAtRoute": "/network/reports/scheduled",
                    "position": "right"
                },
                {
                    "kind": "standard",
                    "title": "Scheduled reports",
                    "body": "View, edit and manage your scheduled reports, or delete reports that are no longer required.",
                    "selector": "[data-tour-step=\"scheduled-reports-page\"]",
                    "overflowPosition": "top-center",
                    "noBack": true
                },
                {
                    "kind": "standard",
                    "title": "Create a scheduled report",
                    "body": "Create a recurring report by selecting a report type, date range and device group. Reports can also be sent to additional recipients, including people outside of Illuminate.",
                    "selector": "[data-tour-step=\"scheduled-reports-add\"]"
                }
            ]
        }
    }
}
